import React, { useEffect, useRef, useState } from 'react';
import StatBox from '../ui/StatBox';
import { GameIntro, GameOver, GameState, isTypingKey, useSaveGameResult } from './GameShell';
import { playCorrectSound, playWrongSound, playSuccessSound } from '../../utils/audio';
import { User } from '../../types';
import { ScorePops, useScorePops } from '../ui/ScorePops';

// Pictures chosen to be unambiguous at a glance for young readers.
const LEVELS: [string, string][][] = [
  [['cat', '🐱'], ['dog', '🐶'], ['sun', '☀️'], ['bus', '🚌'], ['pig', '🐷'], ['egg', '🥚'], ['car', '🚗'], ['bee', '🐝'], ['cow', '🐮'], ['hat', '🎩'], ['fox', '🦊'], ['owl', '🦉'], ['ant', '🐜'], ['key', '🔑']],
  [['fish', '🐟'], ['frog', '🐸'], ['moon', '🌙'], ['star', '⭐'], ['cake', '🍰'], ['bear', '🐻'], ['duck', '🦆'], ['book', '📖'], ['ball', '⚽'], ['tree', '🌳'], ['lion', '🦁'], ['milk', '🥛'], ['drum', '🥁'], ['ship', '🚢'], ['corn', '🌽'], ['bell', '🔔']],
  [['apple', '🍎'], ['house', '🏠'], ['train', '🚆'], ['pizza', '🍕'], ['horse', '🐴'], ['snake', '🐍'], ['whale', '🐋'], ['clock', '🕐'], ['robot', '🤖'], ['tiger', '🐯'], ['bread', '🍞'], ['shark', '🦈'], ['zebra', '🦓'], ['crown', '👑'], ['ghost', '👻'], ['snail', '🐌']],
  [['rocket', '🚀'], ['monkey', '🐵'], ['banana', '🍌'], ['turtle', '🐢'], ['rabbit', '🐰'], ['carrot', '🥕'], ['flower', '🌸'], ['rainbow', '🌈'], ['penguin', '🐧'], ['giraffe', '🦒'], ['octopus', '🐙'], ['dolphin', '🐬'], ['pumpkin', '🎃'], ['volcano', '🌋'], ['unicorn', '🦄'], ['tractor', '🚜']],
  [['umbrella', '☂️'], ['elephant', '🐘'], ['dinosaur', '🦕'], ['butterfly', '🦋'], ['pineapple', '🍍'], ['strawberry', '🍓'], ['crocodile', '🐊'], ['snowman', '⛄'], ['hamburger', '🍔'], ['kangaroo', '🦘'], ['ladybug', '🐞'], ['sandwich', '🥪'], ['helicopter', '🚁']],
];
const ROUNDS = 15;
const WORDS_PER_LEVEL = 3;

interface Round { word: string; emoji: string }

const buildRounds = (): Round[] => {
  const used = new Set<string>();
  return Array.from({ length: ROUNDS }, (_, i) => {
    const pool = LEVELS[Math.min(LEVELS.length - 1, Math.floor(i / WORDS_PER_LEVEL))].filter(([w]) => !used.has(w));
    const [word, emoji] = pool[Math.floor(Math.random() * pool.length)];
    used.add(word);
    return { word, emoji };
  });
};

const speak = (word: string) => {
  if (!('speechSynthesis' in window)) return;
  window.speechSynthesis.cancel();
  const u = new SpeechSynthesisUtterance(word);
  u.rate = 0.8;
  window.speechSynthesis.speak(u);
};

const PictureSpellGame: React.FC<{ user?: User }> = ({ user }) => {
  const [gameState, setGameState] = useState<GameState>('ready');
  const [rounds, setRounds] = useState<Round[]>([]);
  const [index, setIndex] = useState(0);
  const [typed, setTyped] = useState('');
  const [wrongOnSlot, setWrongOnSlot] = useState(0);
  const [roundMistakes, setRoundMistakes] = useState(0);
  const [score, setScore] = useState(0);
  const [perfectWords, setPerfectWords] = useState(0);
  const [mistakes, setMistakes] = useState(0);
  const [shake, setShake] = useState(false);
  const [celebrate, setCelebrate] = useState(false);
  const startedAt = useRef(0);
  const { pops, pop } = useScorePops();
  // Read by the key handler so back-to-back keystrokes never see stale state.
  const live = useRef({ typed: '', roundMistakes: 0, locked: false });
  const [elapsed, setElapsed] = useState(0);

  const startGame = () => {
    setRounds(buildRounds());
    live.current = { typed: '', roundMistakes: 0, locked: false };
    setIndex(0); setTyped(''); setWrongOnSlot(0); setRoundMistakes(0);
    setScore(0); setPerfectWords(0); setMistakes(0); setCelebrate(false);
    startedAt.current = Date.now();
    setGameState('playing');
  };

  useSaveGameResult(user, 'picture-spell', gameState, score);

  const round = rounds[index];

  useEffect(() => {
    if (gameState !== 'playing') return;
    const t = setInterval(() => setElapsed(Math.floor((Date.now() - startedAt.current) / 1000)), 500);
    return () => clearInterval(t);
  }, [gameState]);

  useEffect(() => {
    if (gameState !== 'playing' || !round || celebrate) return;
    const onKey = (e: KeyboardEvent) => {
      if (!isTypingKey(e)) return;
      e.preventDefault();
      const lv = live.current;
      if (lv.locked) return;
      const expected = round.word[lv.typed.length];
      if (e.key.toLowerCase() !== expected) {
        lv.roundMistakes++;
        playWrongSound();
        setMistakes(m => m + 1);
        setRoundMistakes(m => m + 1);
        setWrongOnSlot(w => w + 1);
        setShake(true);
        setTimeout(() => setShake(false), 400);
        return;
      }
      const next = lv.typed + expected;
      lv.typed = next;
      setTyped(next);
      setWrongOnSlot(0);
      if (next.length < round.word.length) { playCorrectSound(); return; }

      // Word complete
      playSuccessSound();
      const perfect = lv.roundMistakes === 0;
      lv.locked = true;
      setScore(sc => sc + round.word.length * 10 + (perfect ? 20 : 0));
      pop(`+${round.word.length * 10 + (perfect ? 20 : 0)}`, 50, 18, perfect ? '#16a34a' : '#f59e0b');
      if (perfect) setPerfectWords(p => p + 1);
      setCelebrate(true);
      setTimeout(() => {
        setCelebrate(false);
        live.current = { typed: '', roundMistakes: 0, locked: false };
        setTyped(''); setWrongOnSlot(0); setRoundMistakes(0);
        if (index + 1 >= rounds.length) setGameState('over');
        else setIndex(i => i + 1);
      }, 900);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [gameState, round, typed, roundMistakes, celebrate, index, rounds.length, pop]);

  if (gameState === 'ready') {
    return (
      <GameIntro icon="🐝" title="Picture Spell" skill="spelling -- turning a picture into the right letters." accent="text-amber-600" button="bg-gradient-to-r from-amber-400 to-orange-500" onStart={startGame}>
        A picture appears with one box for each letter. Type the word that matches the picture. Stuck on a letter? Miss it twice and a hint appears, and the 🔊 button reads the word aloud. Words get longer as you go -- spell all 15!
      </GameIntro>
    );
  }
  if (gameState === 'over') {
    return (
      <GameOver
        title="Spelling Bee Champion!"
        stars={perfectWords >= 12 ? 3 : perfectWords >= 7 ? 2 : 1}
        message={perfectWords >= 12 ? 'Wow, almost every word perfect!' : 'Great spelling -- try for more perfect words!'}
        stats={[{ label: 'Score', value: score }, { label: 'Perfect Words', value: `${perfectWords}/${ROUNDS}` }, { label: 'Mistakes', value: mistakes }, { label: 'Time', value: `${elapsed}s` }]}
        button="bg-gradient-to-r from-amber-400 to-orange-500"
        onRestart={startGame}
      />
    );
  }
  if (!round) return null;

  return (
    <div className="w-full">
      <div className="flex flex-wrap justify-around items-center mb-4 gap-3 p-3 bg-white/60 rounded-2xl shadow-inner">
        <StatBox label="Word" value={`${index + 1}/${ROUNDS}`} />
        <StatBox label="Score" value={score} />
        <StatBox label="Perfect" value={perfectWords} />
        <StatBox label="Time" value={`${elapsed}s`} />
      </div>
      <div className="relative rounded-2xl border-4 border-white shadow-inner bg-gradient-to-b from-yellow-100 via-amber-100 to-lime-100 py-10 px-4 flex flex-col items-center select-none">
        <div className={`text-[7rem] leading-none mb-6 transition-transform duration-300 ${celebrate ? 'scale-125 rotate-6' : ''}`} aria-hidden="true">{round.emoji}</div>
        <button onClick={() => speak(round.word)} className="mb-6 inline-flex items-center gap-1.5 text-sm font-semibold text-amber-700 bg-white border border-amber-300 rounded-full px-4 py-1.5 shadow-sm hover:bg-amber-50">
          <span aria-hidden="true">🔊</span> Hear the word
        </button>
        <div className={`flex flex-wrap justify-center gap-2 ${shake ? 'shake' : ''}`}>
          {[...round.word].map((ch, i) => {
            const filled = i < typed.length;
            const isCurrent = i === typed.length;
            const showHint = isCurrent && wrongOnSlot >= 2;
            return (
              <div key={i} className={`w-12 h-14 md:w-14 md:h-16 rounded-xl border-2 flex items-center justify-center font-mono text-3xl font-bold transition-colors
                ${filled ? (celebrate ? 'bg-green-500 border-green-600 text-white' : 'bg-green-100 border-green-400 text-green-700') : isCurrent ? 'bg-white border-amber-500 shadow-md' : 'bg-white/70 border-amber-200'}`}>
                {filled ? ch : showHint ? <span className="text-amber-300">{ch}</span> : ''}
              </div>
            );
          })}
        </div>
        <ScorePops pops={pops} />
        {celebrate && <div className="absolute top-4 right-6 text-2xl font-extrabold text-green-600">{roundMistakes === 0 ? 'Perfect! +20' : 'Nice!'}</div>}
      </div>
      <p className="text-center text-sm text-gray-500 mt-3">Just start typing the word.</p>
    </div>
  );
};

export default PictureSpellGame;
